import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2, Thermometer, Wind, Droplets, Flame, CloudRain, Sunrise, Sunset, ArrowLeft, ExternalLink, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { STATE_HERO, STATE_HERO_DEFAULT } from "@/lib/stateMeta";

const FIRE_COLOR = {
  Low: "text-[#7FB88A] border-[#7FB88A]/40",
  Moderate: "text-amber-300 border-amber-300/40",
  High: "text-orange-400 border-orange-400/40",
  "Very High": "text-red-400 border-red-400/50",
  Unknown: "text-bone/50 border-bone/25",
};

const Stat = ({ icon: Ic, label, value, testid }) => (
  <div className="border border-bone/12 rounded-lg p-4 bg-bone/[0.03]" data-testid={testid}>
    <span className="text-[11px] text-bone/50 flex items-center gap-1.5"><Ic size={13} className="text-blaze" /> {label}</span>
    <p className="font-serif text-3xl text-bone mt-1.5">{value}</p>
  </div>
);

export default function Conditions() {
  const { state } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.conditions(state)
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setData({ available: false, reason: "Unable to load conditions." }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [state]);

  const hero = STATE_HERO[state] || STATE_HERO_DEFAULT;
  const c = data?.current;
  const fire = data?.fire_weather;

  return (
    <div className="bg-[#0d1410] min-h-screen text-bone" data-testid="conditions-page">
      <SiteHeader />

      <section className="relative h-52 sm:h-64 overflow-hidden border-b border-bone/10">
        <img src={hero} alt={`${state} landscape`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1410] via-[#0d1410]/50 to-[#0d1410]/30" />
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-6">
          <button onClick={() => navigate(-1)} className="text-xs text-bone/70 hover:text-blaze inline-flex items-center gap-1.5 mb-3 w-fit" data-testid="conditions-back"><ArrowLeft size={14} /> Back</button>
          <span className="hr-overline text-blaze">Live Conditions</span>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1">{state}</h1>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">
        {loading ? (
          <div className="flex items-center gap-2 text-bone/50 py-16 justify-center"><Loader2 className="animate-spin text-blaze" /> Loading live conditions…</div>
        ) : !data?.available ? (
          <div className="border border-dashed border-bone/20 rounded-lg p-8 text-center" data-testid="conditions-unavailable">
            <AlertTriangle className="text-bone/40 mx-auto mb-3" />
            <p className="text-bone/70">Live conditions are unavailable right now.</p>
            <p className="text-bone/45 text-sm mt-1">{data?.reason || "Please try again shortly."}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="hr-overline text-bone/50">Right now</p>
                <p className="font-serif text-2xl text-bone mt-1">{c.summary}</p>
              </div>
              <div className={`inline-flex items-center gap-2 border rounded-full px-4 py-2 ${FIRE_COLOR[fire?.level] || FIRE_COLOR.Unknown}`} data-testid="conditions-fire">
                <Flame size={16} />
                <span className="text-sm font-semibold">Fire weather: {fire?.level}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              <Stat icon={Thermometer} label="Temperature" value={c.temp != null ? `${Math.round(c.temp)}°F` : "—"} testid="conditions-temp" />
              <Stat icon={Thermometer} label="Feels like" value={c.feels_like != null ? `${Math.round(c.feels_like)}°F` : "—"} testid="conditions-feels" />
              <Stat icon={Wind} label="Wind" value={c.wind != null ? `${Math.round(c.wind)} mph` : "—"} testid="conditions-wind" />
              <Stat icon={Droplets} label="Humidity" value={c.humidity != null ? `${c.humidity}%` : "—"} testid="conditions-humidity" />
            </div>

            <div className="mt-3 border border-bone/12 rounded-lg p-4 bg-bone/[0.02] flex items-start gap-2.5" data-testid="conditions-fire-note">
              <Flame size={15} className="text-blaze mt-0.5 shrink-0" />
              <p className="text-[13px] text-bone/60">{fire?.note}</p>
            </div>

            <h2 className="hr-overline text-bone/50 mt-10 mb-4">5-Day Forecast</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="conditions-forecast">
              {data.forecast.map((d, i) => (
                <div key={i} className="border border-bone/12 rounded-lg p-4 bg-bone/[0.03]" data-testid={`conditions-day-${i}`}>
                  <p className="text-xs text-bone/50">{new Date(d.date).toLocaleDateString(undefined, { weekday: "short" })}</p>
                  <p className="text-[13px] text-bone/80 mt-1 leading-snug h-9">{d.summary}</p>
                  <p className="font-serif text-xl text-bone mt-2">{d.high != null ? `${Math.round(d.high)}°` : "—"} <span className="text-bone/40 text-base">/ {d.low != null ? `${Math.round(d.low)}°` : "—"}</span></p>
                  <div className="mt-3 space-y-1.5 text-[11px] text-bone/55">
                    <p className="flex items-center gap-1.5"><CloudRain size={12} className="text-blaze" /> {d.precip_pct != null ? `${d.precip_pct}%` : "—"} precip</p>
                    <p className="flex items-center gap-1.5"><Wind size={12} className="text-blaze" /> {d.wind_max != null ? `${Math.round(d.wind_max)} mph` : "—"}</p>
                    <p className="flex items-center gap-1.5"><Sunrise size={12} className="text-blaze" /> {d.sunrise || "—"}</p>
                    <p className="flex items-center gap-1.5"><Sunset size={12} className="text-blaze" /> {d.sunset || "—"}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-3 border-t border-bone/10 pt-5">
              <span className="text-xs text-bone/45">Source: {data.source}. Fire-weather is a HomeRange indicator — always verify official restrictions.</span>
              <a href="https://www.nifc.gov/fire-information/nfn" target="_blank" rel="noopener noreferrer" className="text-xs text-blaze inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="conditions-official">Official fire information <ExternalLink size={12} /></a>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
